import { z } from "zod";

/**
 * SÖZLEŞME AI ANALİZİ (KARAR 07 · KARAR 09 · KARAR 11).
 *
 * ### ⚠ İlk dilim YALNIZ analiz + özet
 *
 * KARAR 07: *"İlk dilim yalnız analiz + özettir. Taslak üretimi (sihirbaz) ikinci dilim;
 * risk/madde incelemesi sonraki sprint."* Bu yüzden şemada bir taslak/versiyon girdisi
 * YOK ve olmaması yapısal: yazma şeması olmayan bir sözleşmeden yazma ucu doğmaz
 * (abonelik projeksiyonunda öğrenilen aynı ders).
 *
 * ### ⚠ ÇIKTI YAPILANDIRILMIŞ, serbest metin bloğu değil
 *
 * KARAR 07 beş başlık sayıyor: kısa özet, taraflar, temel yükümlülükler, kritik tarihler,
 * dikkat çeken maddeler. Tek bir uzun metin döndürseydik ekran onu ayrıştırmak zorunda
 * kalır, ayrıştırma modelin o günkü biçimine bağlanır ve model biçim değiştirdiği gün
 * ekran sessizce boşalırdı.
 *
 * ### ⚠ Şema `strict`: model UYDURDUĞU alanı geçiremiyor
 *
 * Model şemanın dışında bir alan üretirse çıktı GEÇERSİZ sayılıyor ve kredi iade ediliyor.
 * Bilinmeyen alanı sessizce atsaydık, modelin ne ürettiğini bilmeden "başarılı" derdik —
 * bu depodaki en pahalı kusur sınıfı (sessiz başarı).
 */

/**
 * Bir taraf.
 *
 * ⚠ `role` BOŞ OLABİLİR: sözleşme tarafın sıfatını yazmamış olabilir ve model
 * "Alıcı" diye bir sıfat uydurmamalı. Zorunlu kılsaydık, uydurmayı ŞART koşardık.
 */
export const contractAiAnalysisPartySchema = z.strictObject({
  name: z.string().min(1).max(300),
  role: z.string().max(300),
});
export type ContractAiAnalysisParty = z.infer<typeof contractAiAnalysisPartySchema>;

/**
 * KAYNAK REFERANSI (dilim 2 · CLOS-205 kriter 4-5 · CLOS-208 kriter 8).
 *
 * ### ⚠ MÜMKÜN OLAN EN DAR ARALIK, TAM METİN DEĞİL
 *
 * Kart: *"özet ve hüküm çıktıları mümkün olan en dar madde, paragraf veya kaynak
 * aralığına referans verir."* `quote` sözleşmenin bir bölümünü kopyalıyor — bu KAYNAK
 * METİN, `description`/`concern` gibi AI YORUMU değil. İkisini ayrı tutmak kartın *"kaynak
 * metin ile AI yorumu ayrımı korunarak sunulur"* kriteri.
 *
 * ⚠ `clause`/`quote` BOŞ OLABİLİR — ZORUNLU DEĞİL: bir iddia belirli bir maddeye
 * bağlanamıyorsa model boş bırakıyor (`confidence: "low"` ile birlikte). Zorunlu kılsaydık
 * model UYDURMAK zorunda kalırdı — tam olarak `criticalDates.date`'teki "tarih uydurma"
 * yasağının kaynak alanındaki karşılığı.
 */
export const contractAiAnalysisSourceRefSchema = z.strictObject({
  /** Madde başlığı/numarası METİNDE YAZDIĞI GİBİ — model kendi numaralandırma uydurmaz. */
  clause: z.string().max(300),
  /** Kaynak metinden BİREBİR alıntı; en dar aralık — bir cümle, bir sözleşmenin tamamı değil. */
  quote: z.string().max(1000),
});
export type ContractAiAnalysisSourceRef = z.infer<typeof contractAiAnalysisSourceRefSchema>;

/**
 * GÜVEN DÜZEYİ (CLOS-205 kriter 6).
 *
 * ⚠ Kart: *"eksik, çelişkili veya düşük güvenli içerik açıkça işaretlenir."* Üç kademe
 * VE SINIRLI (bir sayı/yüzde değil): model kalibre edilmiş bir olasılık üretemez, ama
 * "metinde açıkça yazıyor" ile "yorumlayarak çıkardım" arasındaki fark kullanıcı için
 * anlamlı ve ölçülebilir bir ayrım.
 */
export const contractAiAnalysisConfidenceSchema = z.enum(["high", "medium", "low"]);
export type ContractAiAnalysisConfidence = z.infer<typeof contractAiAnalysisConfidenceSchema>;

export const CONTRACT_AI_ANALYSIS_CONFIDENCE_NAMES: Record<ContractAiAnalysisConfidence, string> = {
  high: "Yüksek güven",
  medium: "Orta güven",
  low: "Düşük güven",
};

export const contractAiAnalysisObligationSchema = z.strictObject({
  /** Yükümlülüğün kimde olduğu; metinden okunamıyorsa boş. */
  party: z.string().max(300),
  description: z.string().min(1).max(2000),
  source: contractAiAnalysisSourceRefSchema,
  confidence: contractAiAnalysisConfidenceSchema,
});
export type ContractAiAnalysisObligation = z.infer<typeof contractAiAnalysisObligationSchema>;

/**
 * Kritik tarih.
 *
 * ### ⚠ `date` ISO'ya ZORLANMIYOR ve bu bilinçli
 *
 * Sözleşmelerin çoğu takvim tarihi değil KOŞUL yazar: *"imzadan itibaren 30 gün içinde"*,
 * *"her yılın ilk çeyreğinde"*. ISO zorunlu kılsaydık model bir takvim tarihi UYDURMAK
 * zorunda kalırdı — ve uydurulmuş bir tarih, tarihsizlikten çok daha tehlikelidir çünkü
 * ekranda gerçek bir tarih gibi görünür.
 *
 * ⚠ Bu alan `contract_dates` (CLOS-113) tablosuna KENDİLİĞİNDEN yazılmıyor: KARAR 11
 * *"kendiliğinden versiyon/karar doğurmaz"* diyor. Kullanıcının kabulüyle doğacak kayıt
 * ikinci dilimin konusu.
 */
export const contractAiAnalysisCriticalDateSchema = z.strictObject({
  label: z.string().min(1).max(300),
  /** Metinde yazdığı gibi: takvim tarihi ya da koşul cümlesi. */
  date: z.string().min(1).max(300),
  note: z.string().max(1000),
  source: contractAiAnalysisSourceRefSchema,
  confidence: contractAiAnalysisConfidenceSchema,
});
export type ContractAiAnalysisCriticalDate = z.infer<typeof contractAiAnalysisCriticalDateSchema>;

export const contractAiAnalysisNotableClauseSchema = z.strictObject({
  title: z.string().min(1).max(300),
  /** Neden dikkat çekici — bir HUKUKİ GÖRÜŞ değil, metinde göze çarpan nokta. */
  concern: z.string().min(1).max(2000),
  source: contractAiAnalysisSourceRefSchema,
  confidence: contractAiAnalysisConfidenceSchema,
});
export type ContractAiAnalysisNotableClause = z.infer<typeof contractAiAnalysisNotableClauseSchema>;

export const contractAiAnalysisOutputSchema = z.strictObject({
  summary: z.string().min(1).max(4000),
  parties: z.array(contractAiAnalysisPartySchema).max(50),
  obligations: z.array(contractAiAnalysisObligationSchema).max(200),
  criticalDates: z.array(contractAiAnalysisCriticalDateSchema).max(100),
  notableClauses: z.array(contractAiAnalysisNotableClauseSchema).max(100),
});
export type ContractAiAnalysisOutput = z.infer<typeof contractAiAnalysisOutputSchema>;

/**
 * Analiz DURUMU.
 *
 * ⚠ Başarısız deneme de KAYIT: kredi işlemindeki `released` satırıyla aynı gerekçe —
 * *"bu iş denendi ve karşılığı alınmadı"* bilgisi denetimin parçası. Yalnız başarılıları
 * saklasaydık, kullanıcının üç kez denediği ve üç kez düşen bir analiz hiç olmamış
 * görünürdü.
 */
export const CONTRACT_AI_ANALYSIS_STATUSES = ["completed", "failed"] as const;
export type ContractAiAnalysisStatus = (typeof CONTRACT_AI_ANALYSIS_STATUSES)[number];

export const CONTRACT_AI_ANALYSIS_STATUS_NAMES: Record<ContractAiAnalysisStatus, string> = {
  completed: "Tamamlandı",
  failed: "Başarısız",
};

/**
 * Başarısızlık SEBEBİ — makine tarafından okunabilir.
 *
 * ⚠ Serbest metin YOK. Sağlayıcının hata gövdesi gönderdiğimiz sözleşme metnini
 * yankılayabilir (Resend'de ölçülen aynı sınıf); onu satıra yazmak, KARAR 09'un
 * *"prompt saklanmaz"* kuralını arka kapıdan delerdi.
 */
export const CONTRACT_AI_ANALYSIS_ERROR_CODES = [
  /** Sağlayıcı çağrısı düştü (ağ, zaman aşımı, HTTP hatası). */
  "provider_error",
  /** Model cevap verdi ama çıktı şemaya uymadı (KARAR 08: sahte başarı yok). */
  "invalid_output",
] as const;
export type ContractAiAnalysisErrorCode = (typeof CONTRACT_AI_ANALYSIS_ERROR_CODES)[number];

export const CONTRACT_AI_ANALYSIS_ERROR_MESSAGES: Record<ContractAiAnalysisErrorCode, string> = {
  provider_error: "AI sağlayıcısına ulaşılamadı; analiz tamamlanmadı ve krediniz iade edildi.",
  invalid_output:
    "AI beklenen biçimde bir çıktı üretmedi; analiz kaydedilmedi ve krediniz iade edildi.",
};

/**
 * ÇIKTININ STATÜSÜ: ÖNERİ (KARAR 11).
 *
 * ### ⚠ Etiket METNİ BURADA, ekranda değil
 *
 * KARAR 11: *"Her AI çıktısı 'AI önerisidir' etiketiyle sunulur."* Etiketi ekrana
 * yazsaydık ikinci bir yüzey açıldığında (ikinci dilim: taslak sihirbazı) orada
 * unutulabilir ya da başka türlü yazılabilirdi — ve sorumluluğun insanda olduğunu söyleyen
 * cümle, ürünün en pahalı cümlesi. Tek kaynak burada; ekran onu okur, uydurmaz
 * (CLAUDE.md kriter 4).
 */
export const AI_OUTPUT_ADVISORY_LABEL = "AI önerisidir";

export const AI_OUTPUT_ADVISORY_DETAIL =
  "Bu çıktı bir AI önerisidir; hukuki sorumluluk kullanıcıdadır. Kendiliğinden bir " +
  "versiyon, tarih ya da karar doğurmaz.";

/**
 * KREDİ HAKKI YOK durumunun kullanıcıya dönük cümlesi (KARAR 12).
 *
 * ⚠ KARAR 12: *"Kişisel alanda AI: kredi hakkı 0 ise motor 'hak yok' der, gizli varsayılan
 * yok."* Ekran bu cümleyi kendi yazsaydı sunucunun reddettiği durumla ekranın anlattığı
 * durum gün gelip ayrışırdı.
 *
 * ⚠ CÜMLE USE-CASE'SİZ. Önceki hâli *"Analiz için paketinize..."* diyordu ve aynı sabit
 * TASLAK üretiminde de dönüyordu: taslak sihirbazında kredisiz kalan kullanıcıya
 * yapmadığı bir işten söz ediliyordu (E2E ile görüldü). Kredi hakkı use-case başına
 * değil ÇALIŞMA ALANI başına; cümle de öyle.
 */
export const AI_NO_CREDIT_MESSAGE =
  "Bu çalışma alanının AI kredi hakkı yok. AI özelliklerini kullanmak için paketinize AI " +
  "kredisi eklenmeli.";

/**
 * VERSİYONUN ANALİZ EDİLEBİLECEK BİR METNİ YOK (CLOS-205 kriter 1 · CLOS-133).
 *
 * ⚠ "Bu sözleşmenin analiz edilebilecek bir metni yok" cümlesinden BİLEREK AYRI: o cümle
 * sözleşmenin hiç değişmez versiyonu olmadığını söylüyor, bu ise VERSİYON var ama onun
 * metni yok (ör. henüz çıkarım koşmadı) diyor. İkisini tek cümlede birleştirseydik
 * kullanıcı "versiyon oluşturayım mı, bekleyeyim mi" sorusuna cevap bulamazdı.
 */
export const AI_NO_ANALYZABLE_VERSION_MESSAGE =
  "Bu sözleşmenin analiz edilebilecek bir değişmez versiyonu yok.";

/**
 * OCR SONUCU HENÜZ HAZIR DEĞİL (CLOS-205 kriter 2-3 · CLOS-229).
 *
 * ### ⚠ Bu, "metin yok" hatasından AYRI ve ADLANDIRILMIŞ bir durum
 *
 * Ölçüm raporu: *"belge 'OCR gerekli' ise CLOS-229 sonucu yoksa adlandırılmış 'hazır
 * değil' hatası."* Genel "metin yok" cümlesine düşürseydik, kullanıcı taranmış bir
 * belgenin OCR'ının sürmekte olduğunu ("biraz sonra tekrar dene") hiç öğrenemez, sanki
 * belge hiç analiz edilemeyecekmiş gibi anlardı.
 */
export const AI_OCR_NOT_READY_MESSAGE =
  "Bu belge taranmış ve metin katmanı yok; OCR sonucu henüz hazır değil. OCR tamamlanınca tekrar deneyin.";

/**
 * Kaynak metnin KÖKENİ (CLOS-205 kriter 3 · CLOS-208 kriter 8).
 *
 * ⚠ `ocr` bilerek AYRI: OCR bir TAHMİN, versiyonun kendi belgesi ya da CLOS-228 deterministik
 * çıkarımı değil. Ekran bunu görmeden OCR'lı bir analizi kesin metinmiş gibi sunardı.
 */
export const AI_ANALYSIS_SOURCE_KINDS = ["document", "extracted", "ocr"] as const;
export type AiAnalysisSourceKind = (typeof AI_ANALYSIS_SOURCE_KINDS)[number];

/**
 * KAYDETME reddi (dilim 2 kök nedeni 2 · CLOS-205 kriter 10).
 *
 * ⚠ Yalnız `completed` bir taslak kaydedilebilir: başarısız bir denemenin kaydedecek bir
 * ÇIKTISI yok — "kaydet" düğmesi orada zaten hiç görünmüyor, ama sunucu istemciye
 * güvenmiyor (CLAUDE.md kriter: istemci tarafından gönderilen tenant/hedef güven kaynağı
 * değildir).
 */
export const AI_ANALYSIS_NOT_SAVABLE_MESSAGE = "Yalnız tamamlanmış bir AI analizi kaydedilebilir.";

/**
 * ⚠ `AI_PROVIDER_DISABLED_MESSAGE` KALDIRILDI (CLOS-1079).
 *
 * Cümle "Analiz şu an çalıştırılamıyor" diyordu ama ON use-case'in hepsi onu
 * kullanıyordu: taslak üretemeyen bir kullanıcıya da "analiz" deniyordu. Yerini
 * `AI_UNAVAILABLE_MESSAGES` aldı — use-case'ten bağımsız ve sebep başına AYRI cümle
 * (`schemas/aiAvailability.ts`).
 */

/** Analizin okunabilir hâli — liste ve detay aynı şekli döndürüyor. */
export type ContractAiAnalysisSummary = {
  id: string;
  contractId: string;
  /** Analizin bağlandığı DEĞİŞMEZ CLOS-133 versiyonu (dilim 2 kök nedeni: kriter 1). */
  contractVersionId: string;
  versionNumber: number;
  /** Kaynak metnin kökeni — OCR'lı analiz ekranda AYRI işaretlenir (kriter 3). */
  sourceKind: AiAnalysisSourceKind;
  /** Yalnız `sourceKind = "ocr"` olduğunda dolu; OCR bir TAHMİN, kesinlik değil. */
  ocrAverageConfidence: number | null;
  status: ContractAiAnalysisStatus;
  /**
   * ⚠ `output` yalnız `completed` durumunda dolu. `failed`'da `null` ve `errorCode` dolu;
   * ikisini aynı anda taşıyan bir satır veritabanı kısıtıyla imkânsız.
   */
  output: ContractAiAnalysisOutput | null;
  errorCode: ContractAiAnalysisErrorCode | null;
  /** Sağlayıcının model kimliği — başarısızda boş dize (satır hiç konuşmadı). */
  model: string;
  /**
   * PROMPT/ŞEMA SÜRÜMÜ (dilim 2 · CLOS-204 kriter 4). `config/ai.json`'dan o ANDAKİ
   * değer kopyalanıyor: sistem yönergesi ya da çıktı şeması kodda değişse bile hangi
   * satırın hangi davranışla üretildiği geriye dönük belli kalır.
   */
  promptVersion: number;
  schemaVersion: number;
  /** Bu analizin DÜŞÜRDÜĞÜ kredi. Başarısızda 0 — iade edildi. */
  credits: number;
  normalizedPages: number;
  /** Hangi katsayı sürümüyle hesaplandı — geçmiş sessizce değişmesin diye. */
  coefficientVersion: number;
  /**
   * ⚠ GÜNCELLİK, HATA DEĞİL: sözleşmenin analizden SONRA yeni bir değişmez versiyonu
   * doğduysa bu analiz artık "en güncel" değil. Okurken versiyon bütünlüğü yeniden
   * doğrulanıyor (kriter 11) — engellemiyor, işaretliyor: değişmez satır hâlâ okunabilir,
   * yalnız ekran onu "güncel değil" diye göstermeli.
   */
  stale: boolean;
  /**
   * ⚠ GEÇİCİ/KALICI AYRIMI (dilim 2 kök nedeni 2). Üretim bu satırı HER ZAMAN yazıyor
   * (kredi/denetim izi kalıcı — KARAR 04), ama `saved: false` iken bu bir TASLAK/ÖNİZLEME:
   * yalnız açık bir `POST .../save` komutu `true`'ya çeviriyor. Kalıcı listede (`GET
   * .../ai-analyses`) yalnız `saved: true` satırlar görünür; taslak yalnız kendi
   * kimliğiyle (`GET .../ai-analyses/:id`) okunabilir — önizleme adımı bunu gerektiriyor.
   */
  saved: boolean;
  savedByName: string | null;
  savedAt: string | null;
  createdByName: string;
  createdAt: string;
};

/**
 * Analiz talebinin gövdesi.
 *
 * ⚠ METİN hâlâ İSTEMCİDEN GELMİYOR, yalnız HANGİ DEĞİŞMEZ VERSİYONUN analiz edileceği
 * geliyor. Sunucu metni versiyonun kanonik içeriğinden okuyor. İstemci metnin kendisini
 * gönderseydi, sözleşmeyi görme yetkisi kontrolünden geçmemiş bir metni analiz ettirmenin
 * yolu açılırdı — ve düşen kredi de o metne yazılırdı.
 *
 * ⚠ `versionId` İSTEĞE BAĞLI: verilmezse servis o ANDAKİ en güncel değişmez versiyonu
 * SEÇİP kaydeder (CLOS-205 kriter 1). Verilirse kullanıcı BİLEREK eski bir versiyonu
 * analiz ediyor — ör. "imzalanan hâli neydi" sorusu güncel çalışma versiyonuyla
 * cevaplanamaz.
 */
export const createContractAiAnalysisSchema = z.strictObject({
  versionId: z.uuid("Geçerli bir versiyon kimliği gerekli").nullish(),
});
export type CreateContractAiAnalysisInput = z.infer<typeof createContractAiAnalysisSchema>;
